import { c as _c } from "react/compiler-runtime";
import { IcoArrowTopRight16, IcoGlobe16, IcoMaximizeSmall16, IcoMinimizeSmall16, IcoMinusSmall16, type Icon } from '@onefootprint/icons';
import type { BrowserSessionResource } from '@onefootprint/request-types/percy';
import { IconButton, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserStatusBadge, browserStatus } from './browser-status';
type BrowserPaneProps = {
  session: BrowserSessionResource;
  handoff: boolean;
  /** Percy is still on its turn; off once it has answered and the browser sits idle. */
  working: boolean;
  /** The pane fills the chat instead of splitting it. */
  wide: boolean;
  onMinimize: () => void;
  onToggleWide: () => void;
  /** Shown under the frame on a phone only, where the pane covers the chat and its pinned cards. */
  footer?: ReactNode;
  /** The empty area under the address bar that the live view layer covers. */
  frameSlotRef: RefObject<HTMLDivElement | null>;
  className?: string;
};
const TOOLTIP_DELAY_MS = 500;
const BrowserPane = t0 => {
  const $ = _c(57);
  const {
    session,
    handoff,
    working,
    wide,
    onMinimize,
    onToggleWide,
    footer,
    frameSlotRef,
    className
  } = t0;
  const {
    t
  } = useTranslation("copilot");
  let t1;
  if ($[0] !== session.current_url) {
    t1 = addressOf(session.current_url);
    $[0] = session.current_url;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    host,
    path
  } = t1;
  let t2;
  if ($[2] !== host || $[3] !== t) {
    t2 = host || t(_temp);
    $[2] = host;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const title = t2;
  let t3;
  if ($[5] !== handoff || $[6] !== working) {
    t3 = browserStatus(handoff, working);
    $[5] = handoff;
    $[6] = working;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const status = t3;
  let t4;
  if ($[8] !== t) {
    t4 = t(_temp2);
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = !wide;
  let t6;
  if ($[10] !== className || $[11] !== t5) {
    t6 = cx("flex flex-col h-full min-w-0 bg-primary", className, {
      "border-l border-solid border-tertiary": t5
    });
    $[10] = className;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IcoGlobe16 color="tertiary" className="shrink-0" />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== title) {
    t8 = <span className="text-primary">{title}</span>;
    $[14] = title;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== path) {
    t9 = <span className="text-tertiary">{path}</span>;
    $[16] = path;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <span className="text-caption-4 truncate min-w-0 flex-1">{t8}{t9}</span>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== status) {
    t11 = <BrowserStatusBadge status={status} />;
    $[21] = status;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== session.current_url || $[24] !== t) {
    t12 = session.current_url.startsWith("http") && <AddressButton icon={IcoArrowTopRight16} label={t(_temp3)} onClick={() => window.open(session.current_url, "_blank", "noopener,noreferrer")} />;
    $[23] = session.current_url;
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== session.current_url || $[27] !== t10 || $[28] !== t11 || $[29] !== t12) {
    t13 = <div className="flex items-center gap-2 min-w-0 flex-1 h-9 md:h-7 pl-2 pr-1 rounded-md bg-secondary border border-solid border-tertiary" title={session.current_url}>{t7}{t10}{t11}{t12}</div>;
    $[26] = session.current_url;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = wide ? IcoMinimizeSmall16 : IcoMaximizeSmall16;
  let t15;
  if ($[31] !== t || $[32] !== wide) {
    t15 = wide ? t(_temp4) : t(_temp5);
    $[31] = t;
    $[32] = wide;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== onToggleWide || $[35] !== t14 || $[36] !== t15) {
    t16 = <ChromeButton className="ml-1 max-md:hidden" icon={t14} label={t15} onClick={onToggleWide} />;
    $[34] = onToggleWide;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t) {
    t17 = t(_temp6);
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== onMinimize || $[41] !== t17) {
    t18 = <ChromeButton className="max-md:ml-0.5" icon={IcoMinusSmall16} label={t17} onClick={onMinimize} />;
    $[40] = onMinimize;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t13 || $[44] !== t16 || $[45] !== t18) {
    t19 = <div className="flex flex-col px-2 py-1.5 shrink-0 border-b border-solid border-tertiary"><div className="flex items-center gap-0.5 h-11 md:h-7">{t13}{t16}{t18}</div></div>;
    $[43] = t13;
    $[44] = t16;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== frameSlotRef) {
    t20 = <div ref={frameSlotRef} className="flex-1 min-h-0 bg-secondary" />;
    $[47] = frameSlotRef;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== footer) {
    t21 = footer && <div className="md:hidden flex flex-col min-h-0 border-t border-solid border-tertiary px-3 pt-3 pb-[max(env(safe-area-inset-bottom),12px)] group-data-[keyboard-open]/app:pb-3">{footer}</div>;
    $[49] = footer;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t19 || $[52] !== t20 || $[53] !== t21 || $[54] !== t4 || $[55] !== t6) {
    t22 = <aside aria-label={t4} className={t6}>{t19}{t20}{t21}</aside>;
    $[51] = t19;
    $[52] = t20;
    $[53] = t21;
    $[54] = t4;
    $[55] = t6;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  return t22;
};
type ChromeButtonProps = {
  icon: Icon;
  label: string;
  onClick: () => void;
  className?: string;
};

// variant={null} drops IconButton's ghost styles so the pressed state can match the address bar's fill.
const chromeButtonClassName = 'bg-transparent border-none hover:bg-hover active:bg-senary/70';
const ChromeButton = t0 => {
  const $ = _c(10);
  const {
    icon,
    label,
    onClick,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx(chromeButtonClassName, "max-md:size-11", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== icon || $[3] !== label || $[4] !== onClick || $[5] !== t1) {
    t2 = <IconButton aria-label={label} icon={icon} variant={null} size="compact" color="secondary" className={t1} onClick={onClick} />;
    $[2] = icon;
    $[3] = label;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== label || $[8] !== t2) {
    t3 = <Tooltip content={label} delayOpen={TOOLTIP_DELAY_MS}>{t2}</Tooltip>;
    $[7] = label;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
type AddressButtonProps = {
  icon: Icon;
  label: string;
  onClick: () => void;
};
const AddressButton = t0 => {
  const $ = _c(7);
  const {
    icon: Icon,
    label,
    onClick
  } = t0;
  let t1;
  if ($[0] !== Icon || $[1] !== label || $[2] !== onClick) {
    t1 = <IconButton aria-label={label} onClick={onClick} icon={Icon} size="tiny" color="tertiary" className="max-md:size-8" />;
    $[0] = Icon;
    $[1] = label;
    $[2] = onClick;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== label || $[5] !== t1) {
    t2 = <Tooltip content={label} delayOpen={TOOLTIP_DELAY_MS}>{t1}</Tooltip>;
    $[4] = label;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const addressOf = (url: string): {
  host: string;
  path: string;
} => {
  try {
    const {
      host,
      pathname,
      search
    } = new URL(url);
    const path = `${pathname}${search}`;
    return {
      host,
      path: path === '/' ? '' : path
    };
  } catch {
    return {
      host: url,
      path: ''
    };
  }
};
export default BrowserPane;
function _temp($) {
  return $.chat.browser.starting;
}
function _temp2($_0) {
  return $_0.chat.resources.browserSession.label;
}
function _temp3($_1) {
  return $_1.chat.browser.openInNewTab;
}
function _temp4($_2) {
  return $_2.chat.browser.collapse;
}
function _temp5($_3) {
  return $_3.chat.browser.expand;
}
function _temp6($_4) {
  return $_4.chat.browser.minimize;
}